import { yupResolver } from '@hookform/resolvers/yup'
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography
} from '@mui/material'
import { useContext, useEffect, useState } from 'react'
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import {
  CheckConflictsInput,
  ConflictResponse,
  ConflictSearchStyle,
  useCheckConflictsLazyQuery
} from '../../../generated'
import { GRAPHQL_QUERY_POLICY, US_STATES } from '../../constants'
import { AppContext } from '../../context'
import { ActionType } from '../../context/AppContextReducer'
import { CardBox } from '../../theme/styleComponents'
import { ConflictCheckSchema } from '../../validationSchema'
import { Alert } from '../common/Alert'
import { CustomController } from '../common/CustomController'
import { EmptyData } from '../common/EmptyData'
import { InputFormFields } from '../externalUsers/InputFormFields'
import ConflictListings from './ConflictListings'

const ConflictCheck = () => {
  const [searchStyle, setSearchStyle] = useState<ConflictSearchStyle>(ConflictSearchStyle.Contains)
  const [conflicts, setConflicts] = useState<ConflictResponse['conflicts']>()
  const [lastSearchData, setLastSearchData] = useState<CheckConflictsInput>()

  const { dispatch, currentPage } = useContext(AppContext)

  const methods = useForm<CheckConflictsInput>({
    resolver: yupResolver(ConflictCheckSchema),
    defaultValues: {
      pagination: {
        page: currentPage,
        limit: 25
      },
      searchStyle: ConflictSearchStyle.Contains
    }
  })

  const {
    handleSubmit,
    control,
    setValue,
    formState: { isSubmitting }
  } = methods

  const handleSearchStyleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchStyle(event.target.value as ConflictSearchStyle)
    setValue('searchStyle', event.target.value as ConflictSearchStyle)
  }

  const [checkConflicts, { loading }] = useCheckConflictsLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onError: error => {
      console.log('conflict errors', error)
      Alert.error('Error checking conflicts')
    },
    onCompleted: data => {
      console.log(data)
      if (data) {
        const { checkConflicts } = data
        const { conflicts, pagination } = checkConflicts
        setConflicts(conflicts as ConflictResponse['conflicts'])

        const { totalPages } = pagination ?? {}
        totalPages && dispatch({ type: ActionType.SET_TOTAL_PAGE_COUNT, totalPageCount: totalPages })
      }
    }
  })

  useEffect(() => {
    if (lastSearchData) {
      // Update the form's pagination
      setValue('pagination.page', currentPage)

      // Trigger a new search with the updated page
      checkConflicts({
        variables: {
          checkConflictsInput: {
            ...lastSearchData,
            pagination: {
              ...lastSearchData.pagination,
              page: currentPage
            }
          }
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, lastSearchData, setValue])

  const onSubmit: SubmitHandler<CheckConflictsInput> = data => {
    const { searchStyle, pagination, addresses, ...rest } = data
    const hasPersonalInfo = Object.entries(rest).some(([_, value]) => !!value)
    const hasAddressInfo = !!addresses?.[0] && Object.values(addresses[0]).some(value => !!value)
    if (!hasPersonalInfo && !hasAddressInfo) {
      Alert.error('At least one field must be filled')
      return
    }
    // A new search starts on page 1. The effect above runs it, so it isn't also requested from here:
    // the API logs a conflict check for every page-1 request.
    dispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
    setLastSearchData({ ...data, pagination: { ...data.pagination, page: 1 } })
  }

  return (
    <>
      <CardBox marginTop={3}>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} px={3} py={1}>
              <Typography variant="h6">Conflict Check System</Typography>
              <Button
                type="submit"
                variant="contained"
                disabled={loading || isSubmitting}
                endIcon={loading && <CircularProgress size={20} color="inherit" />}
              >
                Search
              </Button>
            </Box>

            <Divider />

            <Grid container spacing={3} rowSpacing={1} p={3}>
              <Grid item md={6} sm={12} xs={12}>
                <Box display={'flex'} alignItems={'self-start'} justifyContent={'space-between'} mb={2}>
                  <FormControl>
                    <FormLabel id="search-style-label" sx={{ fontWeight: 'bold' }}>
                      Search Style
                    </FormLabel>
                    <RadioGroup
                      row
                      aria-labelledby="search-style-label"
                      name="searchStyle"
                      value={searchStyle}
                      onChange={handleSearchStyleChange}
                    >
                      <FormControlLabel
                        value={ConflictSearchStyle.Contains}
                        control={<Radio />}
                        label="Contains Text"
                      />
                      <FormControlLabel
                        value={ConflictSearchStyle.StartsWith}
                        control={<Radio />}
                        label="Starts With"
                      />
                      <FormControlLabel value={ConflictSearchStyle.Exact} control={<Radio />} label="Match Word" />
                    </RadioGroup>
                  </FormControl>
                </Box>
              </Grid>

              <Grid item md={12} sm={12} xs={12}>
                <Typography variant="body2" fontSize="18x" sx={{ fontWeight: 'medium' }}>
                  Personal Info
                </Typography>
              </Grid>

              <InputFormFields mdSize={2} />

              <Grid item md={2} sm={12} xs={12}>
                <CustomController
                  controllerName="socialSecurityNumber"
                  controllerLabel="Social Security#"
                  fieldType="text"
                />
              </Grid>

              <Grid item md={2} sm={12} xs={12}>
                <CustomController
                  controllerName="driverLicenseNumber"
                  controllerLabel="Driver License#"
                  fieldType="text"
                />
              </Grid>

              <Grid item md={12} sm={12} xs={12} mt={2}>
                <Typography variant="body2" fontSize="18x" sx={{ fontWeight: 'medium' }}>
                  Address
                </Typography>
              </Grid>

              <Grid item md={6} sm={12} xs={12}>
                <CustomController controllerName="addresses.0.address1" controllerLabel="Address 1" fieldType="text" />
              </Grid>

              <Grid item md={6} sm={12} xs={12}>
                <CustomController controllerName="addresses.0.address2" controllerLabel="Address 2" fieldType="text" />
              </Grid>

              <Grid item md={3} sm={12} xs={12}>
                <CustomController controllerName="addresses.0.city" controllerLabel="City" fieldType="text" />
              </Grid>

              <Grid item md={3} sm={12} xs={12}>
                <FormControl variant="outlined" fullWidth sx={{ marginTop: 1 }}>
                  <InputLabel id="address-state">State</InputLabel>

                  <Controller
                    name="addresses.0.state"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId="address-state"
                        label="State"
                        fullWidth
                        value={field.value || ''}
                        MenuProps={{ style: { maxHeight: 300 } }}
                      >
                        {US_STATES.map((item, index) => {
                          const { name } = item || {}
                          return (
                            <MenuItem value={name} key={`${name}-${index}`}>
                              {name}
                            </MenuItem>
                          )
                        })}
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid item md={3} sm={12} xs={12}>
                <CustomController controllerName="addresses.0.country" controllerLabel="Country" fieldType="text" />
              </Grid>

              <Grid item md={3} sm={12} xs={12}>
                <CustomController controllerName="addresses.0.zipCode" controllerLabel="Zip Code" fieldType="text" />
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </CardBox>

      {/* Conflict Check Results */}
      {loading ? (
        <Box display={'flex'} justifyContent={'center'} alignItems={'center'}>
          <CircularProgress size={20} color="inherit" />
        </Box>
      ) : !conflicts?.length ? (
        <CardBox marginTop={3} py={2}>
          <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} px={3} py={1}>
            <Typography variant="h6">Conflicts</Typography>
          </Box>
          <EmptyData title="No Conflicts!" iconSize="100px" />
        </CardBox>
      ) : (
        <ConflictListings conflicts={conflicts} loading={loading} />
      )}
    </>
  )
}

export default ConflictCheck
